import * as advisors from '../xhr/advisors.js';
import * as types from './types/advisors.js';
import * as notifier from './notifier.js';
import store from '../redux/store.js';
import * as loader from './loader.js';

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 *
 * @param {*} dispatch
 * @param {*} state
 */
function setBankingDetailsVerifying(dispatch, state) {
    dispatch({
        type: types.BANKING_DETAILS_VERIFYING,
        state
    });
}

/**
 * Search advisors by query without updating Redux state
 * @param {string} query
 * @returns {function(*): Promise<Advisor[]>}
 */
export function searchAdvisors(query) {
    return (dispatch) => advisors
        .getAll(0, query)
        .then((data) => data.advisors)
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return [];
        });
}

/**
 *
 * @param {*} query
 * @param {*} nextCursor
 */
export function getAdvisors(query = '', nextCursor = false) {
    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().advisors.cursor : Number(nextCursor);
        if (cursor < 0) {
            return false;
        }

        return advisors
            .getAll(cursor, query)
            .then((data) => {
                dispatch({
                    type: types.GET_ADVISORS_SUCCESS,
                    advisors: data.advisors,
                    cursor: data.advisors.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param brokerage
 */
export function getAllAdvisorsForBrokerage(brokerage) {
    return (dispatch) => advisors
        .getAll(0, '', brokerage)
        .then((data) => data)
        .catch((error) => {
            dispatch(notifier.displayError(error));
        });
}

/**
 *
 * @param {*} advisorID
 */
export function getAdvisor(advisorID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .get(advisorID)
            .then((data) => {
                dispatch({
                    type: types.GET_ADVISOR_SUCCESS,
                    accounts: data.accounts,
                    quotes: data.quotes,
                    cursor: data.accounts.length === 0 ? -1 : data.cursor,
                    specialityProductMetaConfigs: data.specialityProductMetaConfigs,
                    advisorMeta: {
                        ...data.meta,
                        notifications: data.notifications
                    }
                });

                setLoader(dispatch, false);
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * Add a user to the census data, need to know if its a advisor making the request or not
 * @param data
 * @param accountID
 * @returns {function(*): Promise}
 */
export function createAdvisor(data) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .createAdvisor(data)
            .then((response) => {
                dispatch({
                    type: types.CREATE_ADVISOR_SUCCESS,
                    advisor: {
                        ...data,
                        advisorID: response.advisorID,
                        dateAdded: response.dateAdded,
                        numAccounts: 0
                    }
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Advisor added successfully!'));

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));

                return false;
            });
    };
}

/**
 *
 * @param {*} advisorID
 * @param {*} body
 */
export function editAdvisor(advisorID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .updateAdvisor(advisorID, body)
            .then(() => {
                dispatch({
                    type: types.UPDATE_ADVISOR_SUCCESS,
                    advisorID,
                    advisorMeta: body
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Advisor updated successfully!'));

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));

                return false;
            });
    };
}

/**
 * Remove a Advisor from the census data
 * @param AdvisorID
 * @param accountID
 * @returns {function(*): Promise}
 */
export function removeAdvisor(AdvisorID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return advisors
            .removeAdvisor(AdvisorID)
            .then(() => {
                dispatch({
                    type: types.REMOVE_ADVISOR_SUCCESS,
                    AdvisorID
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Advisor has been disabled successfully!'));

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));

                return false;
            });
    };
}

/**
 *
 * @param {*} advisorID
 * @param {*} body
 */
export function createAccount(advisorID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .createAccount(advisorID, body)
            .then((data) => {
                dispatch({
                    type: types.CREATE_ACCOUNT_SUCCESS,
                    data: body
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Successfully created your account.'));

                setTimeout(() => {
                    window.location = `/accounts/${ data.accountID }`;
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param body
 * @returns {function(*=): Promise<boolean | never>}
 */
export function resetPassword(body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .sendForgotPassword(body)
            .then(() => {
                dispatch(notifier.displayMessage('Inform the advisor to check their inbox for a recovery email.'));
                setLoader(dispatch, false);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param institutionNum
 * @param transitNum
 * @returns {function(*): Promise<*>}
 */
export function validateBankingDetails(institutionNum, transitNum) {
    return (dispatch) => {
        setBankingDetailsVerifying(dispatch, true);

        return advisors.validateBankingDetails(institutionNum, transitNum)
            .then((data) => {
                setBankingDetailsVerifying(dispatch, false);
                return data;
            })
            .catch((error) => {
                setBankingDetailsVerifying(dispatch, false);
                dispatch(notifier.displayError(error));
                return error;
            });
    };
}

/**
 * @param advisorID
 * @param groupNum
 * @param expiry
 * @returns {function(*): Promise<boolean>}
 */
export function createMarketplaceAffiliateLink(advisorID, groupNum, expiry) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .createMarketplaceAffiliateLink(advisorID, groupNum, expiry)
            .then((affiliateLinkID) => {
                dispatch({
                    type: types.SET_AFFILIATE_LINK,
                    marketplaceAffiliateLink: {
                        affiliateLinkID,
                        groupNum,
                        expiry
                    }
                });

                dispatch(notifier.displayMessage('Marketplace affiliate link created successfully!'));
                setLoader(dispatch, false);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param advisorID
 * @returns {function(*): Promise<boolean>}
 */
export function deleteMarketplaceAffiliateLink(advisorID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return advisors
            .deleteMarketplaceAffiliateLink(advisorID)
            .then(() => {
                dispatch({
                    type: types.SET_AFFILIATE_LINK,
                    marketplaceAffiliateLink: {}
                });

                dispatch(notifier.displayMessage('Marketplace affiliate link removed successfully!'));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}